import React from 'react';
import {
  Box,
  Pagination,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { setCurrentPage, setItemsPerPage } from '../redux/slices/professionalsSlice';
import './ProfessionalsPagination.css';

const ProfessionalsPagination = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();
  
  const paginationData = useSelector((state) => {
    const professionalsState = state.professionals;
    return {
      filteredProfessionals: professionalsState.professionals || [],
      currentPage: professionalsState.currentPage || 1,
      itemsPerPage: professionalsState.itemsPerPage || 12,
      totalPages: Math.ceil((professionalsState.professionals?.length || 0) / (professionalsState.itemsPerPage || 12))
    };
  });
  
  const { filteredProfessionals, currentPage, itemsPerPage, totalPages } = paginationData;

  const handlePageChange = (event, page) => {
    dispatch(setCurrentPage(page));
    // Scroll to top when page changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (event) => {
    const newItemsPerPage = event.target.value;
    dispatch(setItemsPerPage(newItemsPerPage));
    dispatch(setCurrentPage(1)); // Reset to first page
  };

  const startIndex = (currentPage - 1) * itemsPerPage + 1;
  const endIndex = Math.min(currentPage * itemsPerPage, filteredProfessionals.length);

  if (filteredProfessionals.length === 0) {
    return null;
  }

  return (
    <Box
      className="professionals-pagination"
      sx={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 2,
        mt: 4,
        p: 3,
        backgroundColor: 'background.paper',
        borderRadius: '15px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      }}
    >
      {/* Results Info */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            fontWeight: 500,
            whiteSpace: 'nowrap',
          }}
        >
          Affichage de {startIndex} à {endIndex} sur {filteredProfessionals.length} professionnels
        </Typography>
        
        {/* Items per page selector */}
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Par page</InputLabel>
          <Select
            value={itemsPerPage}
            onChange={handleItemsPerPageChange}
            label="Par page"
            sx={{
              borderRadius: '10px',
              '& .MuiSelect-select': {
                py: 1,
              },
            }}
          >
            <MenuItem value={6}>6</MenuItem>
            <MenuItem value={12}>12</MenuItem>
            <MenuItem value={24}>24</MenuItem>
            <MenuItem value={48}>48</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <Pagination
          count={totalPages}
          page={currentPage}
          onChange={handlePageChange}
          color="primary"
          size={isMobile ? 'small' : 'medium'}
          showFirstButton
          showLastButton
          sx={{
            '& .MuiPaginationItem-root': {
              borderRadius: '10px',
              fontWeight: 500,
              transition: 'all 0.2s ease',
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)',
              },
            },
            '& .MuiPaginationItem-page.Mui-selected': {
              background: 'linear-gradient(135deg, #1976D2 0%, #1565C0 100%)',
              color: 'white',
              fontWeight: 600,
              '&:hover': {
                background: 'linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)',
              },
            },
            '& .MuiPaginationItem-ellipsis': {
              color: 'text.secondary',
            },
          }}
        />
      )}
    </Box>
  );
};

export default ProfessionalsPagination;